// ─── PER-SCENE MEMORY ───────────────────────────────────────────────────────
//
// Haley, 2026-08-02, twice: regenerating and replying "needs to also not
// affect other scenes messages", and "ai characters should only remember the
// scenes they are in in that story/channel".
//
// Filtering the prompt HISTORY by scene was not enough. The rolling summary
// bypassed that wall completely: `rolling_summary` is a single column on the
// `channels` row, built from every message in the channel and injected into
// EVERY character's prompt as "CANONICAL ... ground truth". So Cruz's prompt
// carried a summary of Nicole's Lance scene no matter how carefully the raw
// history was filtered — and a summary is worse than raw history here, because
// it is compressed, authoritative, and unattributed.
//
// One summary per (channel, scene). Same summariser, same prompt, separate
// slot. A scene remembers itself and nothing else.

import { supabase } from './supabaseClient.js';
import { safeGetSession } from './supabaseClient.js';
import { AI_ABORT_MS } from './aiTimeouts.js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Mirrors rollingSummary.js — refresh every N new messages IN THE SCENE, not
// in the channel, or a busy parallel scene would drag this one's counter up
// and trigger refreshes with nothing new to say.
const SUMMARIZE_EVERY_N = 40;
const RECENT_TAIL = 60;

const SUMMARIZER_PROMPT = `You compress a roleplay scene into a factual summary another writer can use as ground truth.
Keep: who is present, where they are, what has actually happened, what was promised or threatened, what is unresolved, and where the emotional standing between them now sits.
Drop: prose flourishes, repeated beats, anything you are not certain of.
Never invent. Never soften anyone. Write plain declarative sentences, no headings, under 250 words.`;

export function shouldRefreshSceneSummary(sceneMsgCount, lastAtCount) {
  if (!sceneMsgCount || sceneMsgCount < SUMMARIZE_EVERY_N) return false;
  return (sceneMsgCount - (lastAtCount || 0)) >= SUMMARIZE_EVERY_N;
}

export async function getSceneSummary(channelId, sceneTag) {
  if (!channelId || !sceneTag) return null;
  try {
    const { data, error } = await supabase
      .from('scene_summaries')
      .select('summary, msg_count, updated_at')
      .eq('channel_id', channelId)
      .eq('scene_tag', sceneTag)
      .maybeSingle();
    if (error) return null;
    return data || null;
  } catch {
    return null;
  }
}

export function formatSceneSummaryForPrompt(row, characterName, playerLabel) {
  if (!row?.summary) return '';
  // Dated, like every other memory line (feedback_mfml_dm_old_scenes): any
  // "tonight" inside it was written as of this date, not as of now.
  const asOf = row.updated_at ? String(row.updated_at).slice(0, 10) : '';
  return `\n═══ THIS SCENE SO FAR — rolling summary for ${characterName} and ${playerLabel || 'the player'}${asOf ? ` (as of ${asOf})` : ''} ═══\n`
    + `A CANONICAL summary of earlier messages IN THIS SCENE that you can no longer see verbatim. Ground truth for what has happened, what is unresolved, and where you stand with them. It covers this scene only — other scenes in this room are not yours and are not here.\n\n`
    + `${row.summary}\n`;
}

/**
 * Refresh the summary for ONE scene. Fire-and-forget.
 * `messages` must already be filtered to the scene by the caller.
 */
// One at a time per scene, for the same reason as rollingSummary.js: these
// only write msg_count when they finish, and a human send now triggers them.
const _sceneInFlight = new Set();

export async function refreshSceneSummary({
  channelId, sceneTag, characterName, playerLabel, messages, totalCount, priorSummary,
}) {
  if (!channelId || !sceneTag || !messages?.length) return;
  const inFlightKey = `${channelId}:${sceneTag}`;
  if (_sceneInFlight.has(inFlightKey)) return;
  _sceneInFlight.add(inFlightKey);
  try {
    const { data: { session } = { session: null } } = await safeGetSession().catch(() => ({ data: { session: null } }));
    if (!session?.access_token) return;

    const tail = messages.slice(-RECENT_TAIL).map(m => {
      const speaker = m.is_ai_character
        ? (m.speaker || m.character_id || characterName)
        : (m.senderProfile?.display_name || playerLabel || 'Player');
      return `${speaker}: ${String(m.content || '').slice(0, 600)}`;
    }).join('\n');
    if (!tail.trim()) return;

    const input = priorSummary
      ? `PRIOR SUMMARY (refresh and extend):\n${priorSummary}\n\n${'-'.repeat(40)}\n\nNEW MESSAGES SINCE PRIOR SUMMARY:\n${tail}`
      : `RECENT MESSAGES (no prior summary exists — produce a fresh summary):\n${tail}`;

    const res = await fetch(`${SUPABASE_URL}/functions/v1/ai-proxy`, {
      method: 'POST',
      // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(AI_ABORT_MS),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        provider: 'claude-haiku',
        nsfw: true,
        systemPrompt: SUMMARIZER_PROMPT,
        messages: [{ role: 'user', content: input }],
        maxTokens: 600,
        feature: 'scene_summary',
        characterId: null,
        characterName,
      }),
    });
    if (!res.ok) return;
    const data = await res.json();
    const summary = (data.text || '').trim();
    if (!summary) return;

    await supabase.from('scene_summaries').upsert({
      channel_id: channelId,
      scene_tag: sceneTag,
      summary,
      msg_count: totalCount,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'channel_id,scene_tag' });
  } catch (err) {
    console.warn('[sceneSummary] refresh failed (non-fatal):', err);
  } finally {
    _sceneInFlight.delete(inFlightKey);
  }
}

// ─── SCENE FLAGS — one-way latches for facts a scene has established ───────
//
// Audit 2026-08-04 (P1-9 root fix): "the Fates route" recognition lived in a
// 40-message sliding scan of recent messages. When the recognition line
// scrolled out of the window the whole THIS IS A FATES ROUTE block silently
// vanished — the character "forgot she's Lachesis" mid-scene — and the
// re-scan also made the CACHED prompt half drift every turn. A persisted
// latch fixes both: recognition survives the window forever, and the value
// is byte-stable for the cache.
//
// Scene-scoped on purpose ("This applies to THIS route only"). Rooms with no
// scene dimension use the '__room__' sentinel.

export async function getSceneFlags(channelId, sceneTag) {
  if (!channelId) return {};
  try {
    const { data } = await supabase
      .from('scene_summaries')
      .select('flags')
      .eq('channel_id', channelId)
      .eq('scene_tag', sceneTag || '__room__')
      .maybeSingle();
    return data?.flags || {};
  } catch { return {}; }
}

/** Merge one flag in (upsert; fire-and-forget safe). Latches never unset. */
export async function setSceneFlag(channelId, sceneTag, key, value) {
  if (!channelId || !key) return;
  try {
    const tag = sceneTag || '__room__';
    const { data: existing } = await supabase
      .from('scene_summaries')
      .select('flags')
      .eq('channel_id', channelId)
      .eq('scene_tag', tag)
      .maybeSingle();
    const flags = { ...(existing?.flags || {}), [key]: value };
    const { error } = await supabase
      .from('scene_summaries')
      .upsert({ channel_id: channelId, scene_tag: tag, flags }, { onConflict: 'channel_id,scene_tag' });
    if (error) console.warn('[sceneSummary] flag upsert failed:', error.message);
  } catch (e) {
    console.warn('[sceneSummary] setSceneFlag error:', e?.message || e);
  }
}
